import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import { esDe } from "./pulsos";

// ─────────────────────────────────────────────────────────────────────────────
// PULSO DEL NEGOCIO (27-ago-2026)
// Tablero ejecutivo del Dashboard: el pulso financiero de un vistazo, reuniendo
// los módulos nuevos de la semana (Finanzas, Cuadre SII, Pagos, F29, Cotizaciones,
// Concentración de clientes). SOLO admin/accountant — si el rol no es contable,
// el componente no renderiza nada (return null).
//
// Cada tarjeta:
//   · número grande + sub-línea de contexto + color semáforo
//   · onClick que navega al módulo dueño del dato (useNavigate)
//   · estado de carga PROPIO — cada endpoint se pide por separado; si uno tarda
//     o falla, su tarjeta muestra "—"/skeleton y las demás no se bloquean.
//
// Endpoints consumidos (todos ya existen; acá SOLO se leen):
//   1. Utilidad del mes ....... GET /finanzas/resultado?ano=YYYY  (meses[mesActual])
//   2. Ventas del mes ......... = misma /finanzas/resultado (venta neta del mes)
//      + por seguir hoy ....... GET /quotation/seguimiento/pendientes
//   3. Por cobrar ............. GET /sii/cuadre           (resumen.saldo_pendiente)
//   4. Top 3 piezas del mes ... GET /sii/ventas-piezas?meses=1  (items[0..2])
//   5. Concentración Rocmin ... GET /sii/ventas-clientes?meses=1 (pct del cliente)
//
// DEDUPE (28-ago): Pagos, F29, excepciones y "OC por registrar" SALIERON de este
// pulso. Son cola de trabajo del contable y del taller, y el admin las veía dos veces
// —acá y en el pulso dueño—. Quién muestra cada indicador está en ./pulsos.js, y esa
// tabla gobierna tanto el render como el FETCH: lo que no se muestra, no se pide.
// El código de esas tarjetas quedó en su lugar, detrás de su guardia, para que
// devolverlas a este pulso sea cambiar una línea de la tabla y nada más.
// ─────────────────────────────────────────────────────────────────────────────

const SEM = { green: "#2ecc71", red: "#e74c3c", amber: "#f39c12", blue: "#3498db", purple: "#a78bfa", gray: "#8a8a8a" };

// Monto compacto para el número grande (millones → "M"); exacto en el title/tooltip.
const clpC = v => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toLocaleString("es-CL", { maximumFractionDigits: 1 })}M`;
  return `$${Math.round(n).toLocaleString("es-CL")}`;
};
const clp = v => (v == null || isNaN(v) ? "—" : `$${Math.round(Number(v)).toLocaleString("es-CL")}`);

const PulseNegocio = ({ isMobile }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const hasRole = auth?.can?.("ver_pulso_contable") || false;

  const [d, setD]   = useState({}); // key -> objeto de datos (o null si falló)
  const [ld, setLd] = useState({}); // key -> bool cargando

  useEffect(() => {
    if (!hasRole) return;
    const now = new Date();
    const ano = now.getFullYear();
    const mesIdx = now.getMonth();                                   // 0-based → meses[mesIdx]
    const periodo = `${ano}-${String(mesIdx + 1).padStart(2, "0")}`; // YYYY-MM

    // Cada fetch es independiente: marca su loading, guarda su resultado (o null),
    // y nunca tumba a los demás (degradación elegante por tarjeta).
    const run = (key, promise, transform) => {
      setLd(s => ({ ...s, [key]: true }));
      promise
        .then(res => setD(s => ({ ...s, [key]: transform(res.data) })))
        .catch(() => setD(s => ({ ...s, [key]: null })))
        .finally(() => setLd(s => ({ ...s, [key]: false })));
    };

    run("resultado", backend.get(`finanzas/resultado?ano=${ano}`), data => {
      const mes = (data?.meses || [])[mesIdx] || null;
      return mes ? { utilidad: mes.utilidad, venta: mes.venta, pe: mes.puntoEquilibrio } : null;
    });
    run("segui", backend.get("quotation/seguimiento/pendientes"),
      data => ({ n: Array.isArray(data) ? data.length : 0 }));
    run("cuadre", backend.get("sii/cuadre"),
      data => ({ saldo: data?.resumen?.saldo_pendiente || 0, n: data?.resumen?.con_saldo || 0 }));
    // Pagos, F29, excepciones y OC por registrar ya NO se piden acá: son cola de
    // trabajo del contable y del taller, y viven en sus pulsos (ver ./pulsos.js).
    // El `esDe` gobierna también el fetch, no sólo el render: si un indicador no se
    // muestra, tampoco se pide — el dashboard hacía cuatro llamadas de más por carga.
    if (esDe("negocio", "pagos")) {
      run("pagos", backend.get("sii/pagos?meses=3"), data => ({
        venc: data?.resumen?.vencidas   || { n: 0, monto: 0 },
        pv:   data?.resumen?.por_vencer || { n: 0, monto: 0 },
      }));
    }
    if (esDe("negocio", "f29")) {
      run("f29", backend.get(`libro/f29?periodo=${periodo}`), data => {
        const l = (data?.lineas || []).find(x => x.codigo === "91");
        return { iva: l ? l.monto : null };
      });
    }
    if (esDe("negocio", "excepciones")) {
      run("exc", backend.get("libro/excepciones"),
        data => ({ n: Array.isArray(data) ? data.length : 0 }));
    }
    run("piezas", backend.get("sii/ventas-piezas?meses=1"),
      data => ({ items: (data?.items || []).slice(0, 3) }));
    if (esDe("negocio", "ocPorRegistrar")) {
      // OC llegadas a recepcion@ sin registrar en el ERP (vigía IMAP del backend).
      run("ocMail", backend.get("mailEntrante/pendientes/count"),
        data => ({ n: data?.n || 0 }));
    }
    run("clientes", backend.get("sii/ventas-clientes?meses=1"), data => {
      const cs = data?.clientes || [];
      const roc = cs.find(c => /rocmin/i.test(c.razon || ""));
      const top = cs[0] || null;
      return roc
        ? { pct: roc.pct, razon: roc.razon, esRocmin: true }
        : { pct: top?.pct ?? null, razon: top?.razon || null, esRocmin: false };
    });
  }, [hasRole]);

  if (!hasRole) return null;

  // ── tarjeta KPI genérica ────────────────────────────────────────────────────
  const Kpi = ({ icon, titulo, valor, sub, color, to, loading, urgente, title, children }) => (
    <div
      onClick={() => to && navigate(to)}
      className="t-02 tap-card"
      title={title || undefined}
      onMouseEnter={e => { if (!isMobile) e.currentTarget.style.borderColor = "#3a3a3a"; }}
      onMouseLeave={e => { if (!isMobile) e.currentTarget.style.borderColor = urgente ? "#5a2020" : "#2e2e2e"; }}
      style={{
        background: urgente ? "#241416" : "#242424",
        border: `1px solid ${urgente ? "#5a2020" : "#2e2e2e"}`,
        borderRadius: 12, padding: "13px 14px", cursor: "pointer",
        minWidth: 0, display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".06em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {icon} {titulo}
        </span>
        <span style={{ color: "#555", fontSize: 14, lineHeight: 1, flexShrink: 0 }}>›</span>
      </div>
      {children ? children : (
        loading
          ? <div style={{ height: 26, width: "60%", background: "#2e2e2e", borderRadius: 6, opacity: 0.6 }} />
          : <div style={{ fontSize: valor == null ? 14 : 24, fontWeight: 800, color: valor == null ? "#8a8a8a" : color, lineHeight: 1.1, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{valor == null ? "—  sin datos" : valor}</div>
      )}
      {sub && <div style={{ fontSize: 10.5, color: "#8a8a8a", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
    </div>
  );

  // ── datos derivados por tarjeta (con degradación) ───────────────────────────
  const r = d.resultado;
  const util = r ? r.utilidad : null;
  const utilColor = r ? (util >= 0 ? SEM.green : SEM.red) : "#fff";
  const utilSub = r
    ? `${(r.venta ?? 0) >= (r.pe ?? 0) ? "▲ sobre" : "▼ bajo"} equilibrio · venta ${clpC(r.venta)} vs ${clpC(r.pe)}`
    : "sin datos de finanzas este mes";

  const seg = d.segui;
  const ventaSub = seg && seg.n != null
    ? `${seg.n} cotización${seg.n === 1 ? "" : "es"} por seguir hoy`
    : "seguimiento de cotizaciones —";

  const cu = d.cuadre;
  const pa = d.pagos;
  const porPagar = pa ? (pa.venc.monto || 0) + (pa.pv.monto || 0) : null;
  const pagarColor = pa ? (pa.venc.monto > 0 ? SEM.red : porPagar > 0 ? SEM.amber : SEM.green) : "#fff";
  const pagarUrgente = !!(pa && pa.venc.n > 0);

  const f29 = d.f29;
  const exc = d.exc;
  const ivaSub = exc && exc.n != null
    ? `${exc.n} excepción${exc.n === 1 ? "" : "es"} contable${exc.n === 1 ? "" : "s"} por revisar`
    : "borrador F29 del mes";

  const pz = d.piezas;
  const cl = d.clientes;
  const rocColor = cl && cl.pct != null
    ? (cl.pct > 70 ? SEM.red : cl.pct > 50 ? SEM.amber : SEM.green)
    : "#fff";
  const rocSub = cl
    ? (cl.esRocmin
        ? "de la venta del mes es Rocmin (riesgo #1)"
        : (cl.razon ? `top: ${cl.razon} · sin Rocmin este mes` : "sin ventas este mes"))
    : "concentración de clientes —";

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "2px 0 9px" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: ".07em" }}>
          Pulso del negocio
        </span>
        <span style={{ fontSize: 10, color: "#777" }}>
          {new Date().toLocaleDateString("es-CL", { month: "long", year: "numeric" })}
        </span>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(4, minmax(0, 1fr))",
        gap: 8,
      }}>
        {/* 1 · Utilidad del mes */}
        <Kpi icon="💰" titulo="Utilidad del mes" to="/contabilidad?tab=finanzas"
          loading={ld.resultado} valor={clpC(util)} color={utilColor} sub={utilSub}
          title={r ? `Utilidad neta ${clp(util)} · venta ${clp(r.venta)} · equilibrio ${clp(r.pe)}` : undefined} />

        {/* 2 · Ventas del mes + por seguir */}
        <Kpi icon="📈" titulo="Ventas del mes" to="/quotations"
          loading={ld.resultado} valor={clpC(r ? r.venta : null)} color={SEM.blue} sub={ventaSub}
          title={r ? `Venta neta del mes ${clp(r.venta)}` : undefined} />

        {/* 3a · Por cobrar */}
        <Kpi icon="📥" titulo="Por cobrar" to="/contabilidad?tab=cuadre"
          loading={ld.cuadre} valor={clpC(cu ? cu.saldo : null)} color={cu && cu.saldo > 0 ? SEM.amber : SEM.green}
          sub={cu ? `${cu.n} OT${cu.n === 1 ? "" : "s"} facturadas con saldo` : "cuadre facturación —"}
          title={cu ? `Saldo sin cobrar ${clp(cu.saldo)}` : undefined} />

        {/* 3b · Por pagar — dueño: pulso contable (es su cola de pago) */}
        {esDe("negocio", "pagos") && (
          <Kpi icon="📤" titulo="Por pagar" to="/contabilidad?tab=pagos" urgente={pagarUrgente}
            loading={ld.pagos} valor={clpC(porPagar)} color={pagarColor}
            sub={pa ? `${pa.venc.n} vencida${pa.venc.n === 1 ? "" : "s"} · ${pa.pv.n} por vencer 7d` : "pagos a proveedores —"}
            title={pa ? `Vencidas ${clp(pa.venc.monto)} + por vencer ${clp(pa.pv.monto)}` : undefined} />
        )}

        {/* 4 · IVA a pagar + excepciones — dueño: pulso contable (hay que declararlo) */}
        {esDe("negocio", "f29") && (
          <Kpi icon="🧾" titulo="IVA a pagar (F29)" to="/contabilidad?tab=libro"
            loading={ld.f29} valor={f29 ? clpC(f29.iva) : "—"} color={SEM.amber} sub={ivaSub}
            title={f29 && f29.iva != null ? `IVA determinado a pagar ${clp(f29.iva)} (borrador)` : "Borrador F29 — revisar antes de declarar"} />
        )}

        {/* 4b · OC por registrar — dueño: pulso del taller (alguien tiene que ingresarla) */}
        {esDe("negocio", "ocPorRegistrar") && (d.ocMail?.n || 0) > 0 && (
          <Kpi icon="📨" titulo="OC por registrar" to="/oc-entrantes" urgente
            loading={ld.ocMail} valor={d.ocMail.n} color={SEM.red}
            sub="llegaron a recepcion@ y no están en el ERP"
            title="Correos con OC detectada que no calzan con ninguna OC registrada — click para resolverlas" />
        )}

        {/* 6 · Concentración Rocmin */}
        <Kpi icon="⚠️" titulo="Concentración Rocmin" to="/contabilidad?tab=clientes" urgente={!!(cl && cl.pct > 70)}
          loading={ld.clientes} valor={cl && cl.pct != null ? `${cl.pct}%` : "—"} color={rocColor} sub={rocSub} />

        {/* 5 · Top 3 piezas del mes (mini-lista) — ocupa 2 columnas */}
        <div style={{ gridColumn: isMobile ? "span 2" : "span 2", display: "flex" }}>
          <Kpi icon="🏅" titulo="Top piezas del mes" to="/contabilidad?tab=piezas">
            {ld.piezas ? (
              <div style={{ height: 26, width: "70%", background: "#2e2e2e", borderRadius: 6, opacity: 0.6 }} />
            ) : !pz || pz.items.length === 0 ? (
              <div style={{ fontSize: 12, color: "#777", padding: "6px 0" }}>Sin ventas de piezas este mes</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 2 }}>
                {pz.items.map((it, i) => (
                  <div key={it.clave || i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, fontSize: 12 }}>
                    <span style={{ color: "#bbb", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      <span style={{ color: "#f39c12", fontWeight: 700, marginRight: 5 }}>{i + 1}</span>
                      {it.sku ? `${it.sku} · ` : ""}{it.nombre}
                    </span>
                    <span style={{ color: "#2ecc71", fontWeight: 700, flexShrink: 0, fontVariantNumeric: "tabular-nums" }} title={clp(it.ingreso)}>
                      {clpC(it.ingreso)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Kpi>
        </div>
      </div>
    </div>
  );
};

export default PulseNegocio;
